import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AuditTrail = () => {
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  const auditEntries = [
    {
      id: 'audit-001',
      timestamp: '2024-12-21T09:15:32Z',
      action: 'Report Generated',
      type: 'report',
      user: 'Sarah Mitchell',
      details: 'EU ETS Surrender Report 2024 generated for all vessels',
      reportType: 'EU ETS',
      hash: '0x1a2b3c4d5e6f7890abcdef1234567890',
      status: 'verified',
      changes: ['Added vessel MV Atlantic Voyager', 'Updated emission factors', 'Verified EUA transactions']
    },
    {
      id: 'audit-002',
      timestamp: '2024-12-21T08:45:18Z',
      action: 'Data Validation',
      type: 'validation',
      user: 'System Automated',
      details: 'Emissions data validated against VMS records',
      reportType: 'FuelEU',
      hash: '0x2b3c4d5e6f7890abcdef1234567890ab',
      status: 'verified',
      changes: ['Validated 1,247 voyage records', 'Cross-checked fuel consumption', 'Verified port call data']
    },
    {
      id: 'audit-003',
      timestamp: '2024-12-21T07:30:45Z',
      action: 'EUA Transaction',
      type: 'transaction',
      user: 'Michael Chen',
      details: 'EUA purchase recorded: 2,500 allowances at €85.50/EUA',
      reportType: 'EU ETS',
      hash: '0x3c4d5e6f7890abcdef1234567890abcd',
      status: 'verified',
      changes: ['Added EUA purchase transaction', 'Updated allowance balance', 'Recorded transaction costs']
    },
    {
      id: 'audit-004',
      timestamp: '2024-12-20T16:22:10Z',
      action: 'Report Submitted',
      type: 'submission',
      user: 'Emma Thompson',
      details: 'IMO DCS Report submitted to flag state authority',
      reportType: 'IMO DCS',
      hash: '0x4d5e6f7890abcdef1234567890abcdef',
      status: 'submitted',
      changes: ['Report submitted to authority', 'Acknowledgment received', 'Compliance status updated']
    },
    {
      id: 'audit-005',
      timestamp: '2024-12-20T14:15:33Z',
      action: 'Data Import',
      type: 'import',
      user: 'System Automated',
      details: 'Noon report data imported from vessel IoT sensors',
      reportType: 'CII Evidence',
      hash: '0x5e6f7890abcdef1234567890abcdef12',
      status: 'verified',
      changes: ['Imported 156 noon reports', 'Updated fuel consumption data', 'Calculated CII metrics']
    },
    {
      id: 'audit-006',
      timestamp: '2024-12-20T11:45:22Z',
      action: 'Configuration Change',
      type: 'config',
      user: 'David Rodriguez',
      details: 'Updated emission factors for 2024 compliance period',
      reportType: 'All Reports',
      hash: '0x6f7890abcdef1234567890abcdef1234',
      status: 'verified',
      changes: ['Updated CO₂ emission factors', 'Applied new regulatory guidelines', 'Recalculated historical data']
    }
  ];

  const getActionIcon = (type) => {
    switch (type) {
      case 'report': return 'FileText';
      case 'validation': return 'CheckCircle';
      case 'transaction': return 'DollarSign';
      case 'submission': return 'Send';
      case 'import': return 'Download';
      case 'config': return 'Settings';
      default: return 'Activity';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'text-success bg-success/10';
      case 'submitted': return 'text-accent bg-accent/10';
      case 'pending': return 'text-warning bg-warning/10';
      case 'failed': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const filteredEntries = auditEntries?.filter(entry => {
    const matchesSearch = entry?.details?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         entry?.user?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         entry?.reportType?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesFilter = filterType === 'all' || entry?.type === filterType;
    return matchesSearch && matchesFilter;
  });

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date?.toLocaleDateString(),
      time: date?.toLocaleTimeString()
    };
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Immutable Audit Trail</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Cryptographically verified record of all emissions and compliance activities
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" size="sm" iconName="Shield" iconPosition="left">
            Verify Integrity
          </Button>
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
            Export Trail
          </Button>
        </div>
      </div>
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Search audit entries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
          />
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant={filterType === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('all')}
          >
            All
          </Button>
          <Button
            variant={filterType === 'report' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('report')}
          >
            Reports
          </Button>
          <Button
            variant={filterType === 'transaction' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('transaction')}
          >
            Transactions
          </Button>
          <Button
            variant={filterType === 'validation' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('validation')}
          >
            Validation
          </Button>
        </div>
      </div>
      {/* Audit Entries */}
      <div className="space-y-4">
        {filteredEntries?.map((entry) => {
          const { date, time } = formatTimestamp(entry?.timestamp);
          return (
            <div
              key={entry?.id}
              className={`maritime-card p-4 cursor-pointer transition-maritime hover:shadow-maritime-md ${
                selectedEntry === entry?.id ? 'ring-2 ring-primary bg-primary/5' : ''
              }`}
              onClick={() => setSelectedEntry(selectedEntry === entry?.id ? null : entry?.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={getActionIcon(entry?.type)} size={16} strokeWidth={2} className="text-primary" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="text-sm font-semibold text-foreground">{entry?.action}</h4>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(entry?.status)}`}>
                        {entry?.status}
                      </div>
                      <span className="text-xs text-muted-foreground">{entry?.reportType}</span>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-2">{entry?.details}</p>
                    
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <span className="flex items-center space-x-1">
                        <Icon name="User" size={12} strokeWidth={2} />
                        <span>{entry?.user}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Icon name="Calendar" size={12} strokeWidth={2} />
                        <span>{date}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Icon name="Clock" size={12} strokeWidth={2} />
                        <span>{time}</span>
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e?.stopPropagation();
                      copyToClipboard(entry?.hash);
                    }}
                    className="p-1"
                  >
                    <Icon name="Copy" size={14} strokeWidth={2} />
                  </Button>
                  <Icon 
                    name={selectedEntry === entry?.id ? "ChevronUp" : "ChevronDown"} 
                    size={16} 
                    strokeWidth={2} 
                    className="text-muted-foreground"
                  />
                </div>
              </div>
              {/* Expanded Details */}
              {selectedEntry === entry?.id && (
                <div className="mt-4 pt-4 border-t border-border space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="text-sm font-semibold text-foreground mb-2">Cryptographic Hash</h5>
                      <div className="flex items-center space-x-2 p-2 bg-muted rounded font-data text-xs">
                        <span className="flex-1 truncate">{entry?.hash}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(entry?.hash)}
                          className="p-1"
                        >
                          <Icon name="Copy" size={12} strokeWidth={2} />
                        </Button>
                      </div>
                    </div>
                    
                    <div>
                      <h5 className="text-sm font-semibold text-foreground mb-2">Entry ID</h5>
                      <div className="flex items-center space-x-2 p-2 bg-muted rounded font-data text-xs">
                        <span className="flex-1">{entry?.id}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(entry?.id)}
                          className="p-1"
                        >
                          <Icon name="Copy" size={12} strokeWidth={2} />
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h5 className="text-sm font-semibold text-foreground mb-2">Changes Made</h5>
                    <div className="space-y-1">
                      {entry?.changes?.map((change, index) => (
                        <div key={index} className="flex items-center space-x-2 text-sm">
                          <Icon name="CheckCircle" size={12} strokeWidth={2} className="text-success" />
                          <span className="text-muted-foreground">{change}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                      <Icon name="Shield" size={12} strokeWidth={2} className="text-success" />
                      <span>Cryptographically verified and immutable</span>
                    </div>
                    <Button variant="outline" size="sm" iconName="ExternalLink" iconPosition="left">
                      View Full Details
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {filteredEntries?.length === 0 && (
        <div className="maritime-card p-8 text-center">
          <Icon name="Search" size={48} strokeWidth={1} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Entries Found</h3>
          <p className="text-muted-foreground">
            No audit entries match your search criteria. Try adjusting your filters.
          </p>
        </div>
      )}
    </div>
  );
};

export default AuditTrail;